<template>
	<section class="view-signup-createaccount" v-bind:class="stateClass">
		<header class="stepped-overlay-header">
			<ChalkyBrandingBadge size="md" />
			<h3>Create an account</h3>
			<p>Dive into the world of ChalkySticks</p>
		</header>

		<section class="fields stepped-overlay-content">
			<fieldset>
				<label>
					<h6>Name</h6>
					<input
						autocomplete="name"
						minlength="6"
						name="name"
						placeholder="John Doe"
						ref="nameInput"
						required
						type="text"
						v-model="authModel.attributes.name"
						v-on:keyup.enter="Handle_OnClickContinue"
					/>
					<p class="type-error" v-if="nameError">{{ nameError }}</p>
				</label>

				<label>
					<h6>Email Address</h6>
					<input
						autocomplete="email"
						minlength="8"
						name="email"
						placeholder="john@example.com"
						required
						type="email"
						v-model="authModel.attributes.email"
						v-on:keyup.enter="Handle_OnClickContinue"
					/>
					<p class="type-error" v-if="emailError">{{ emailError }}</p>
				</label>
			</fieldset>

			<h6 class="push-bottom-xl text-center">Or</h6>

			<ChalkyAuthenticationSocialLogin v-bind:authModel="authModel" v-bind:redirectTo="redirectTo" />
		</section>

		<section class="actions stepped-overlay-actions">
			<button class="skip button-secondary" v-on:click="Handle_OnClickSkip">Skip</button>
			<button class="continue button-primary" v-on:click="Handle_OnClickContinue">Continue</button>
		</section>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Signup
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewSignupCreateAccount extends ViewBase {
		/**
		 * CSS state class based on loading status.
		 *
		 * @return string
		 */
		public get stateClass(): string {
			if (this.loading) {
				return 'state-loading';
			}

			return 'state-default';
		}

		/**
		 * The URL to redirect to after signup
		 *
		 * @return string
		 */
		protected get redirectTo(): string {
			const resolved = this.$router.resolve({
				name: 'PageSignup',
			});

			// Get the full URL (including protocol, host, etc.)
			return window.location.origin + resolved.href + `/favorite-games`;
		}

		/**
		 * @type HTMLInputElement
		 */
		@Ref('nameInput')
		readonly nameInput!: HTMLInputElement;

		/**
		 * @type ChalkySticks.Model.Authentication
		 */
		@Prop()
		protected authModel!: ChalkySticks.Model.Authentication;

		/**
		 * @type string
		 */
		protected emailError: string = '';

		/**
		 * @type boolean
		 */
		protected loading!: boolean;

		/**
		 * @type string
		 */
		protected nameError: string = '';

		/**
		 * @return void
		 */
		@mounted
		protected focusFirstInput(): void {
			this.$nextTick(() => this.nameInput.focus());
		}

		/**
		 * Clears all validation error messages
		 *
		 * @return void
		 */
		protected clearValidationErrors(): void {
			this.nameError = '';
			this.emailError = '';
		}

		/**
		 * Navigates to the next step in the signup process
		 *
		 * @return Promise<boolean>
		 */
		protected async isEmailAvailable(): Promise<boolean> {
			// Loading
			this.loading = true;
			this.$forceUpdate();

			// Check if it exists
			const emailExists = await this.authModel.validateEmail(this.authModel.attributes.email);

			// Simulate delay so it feels like it's working
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Loading
			this.loading = false;
			this.$forceUpdate();

			return emailExists;
		}

		/**
		 * Validates all form inputs and sets appropriate error messages
		 *
		 * @return Promise<boolean>
		 */
		protected async validateFormInputs(): Promise<boolean> {
			const name = this.authModel.attributes.name?.trim() || '';
			const email = this.authModel.attributes.email?.trim() || '';
			let isValid = true;

			// Clear any previous validation errors
			this.clearValidationErrors();

			// Validate name field
			if ((await this.validateNameField(name)) === false) {
				isValid = false;
			}

			// Validate email field
			if ((await this.validateEmailField(email)) === false) {
				isValid = false;
			}

			return isValid;
		}

		/**
		 * Validates the name field and sets error message if invalid
		 *
		 * @param string name
		 * @return Promise<boolean>
		 */
		protected async validateNameField(name: string): Promise<boolean> {
			if (!name) {
				this.nameError = 'Please enter your full name.';
				return false;
			}

			if (name.length < 6) {
				this.nameError = 'Please enter your full name (at least 6 characters).';
				return false;
			}

			return true;
		}

		/**
		 * Validates the email field and sets error message if invalid
		 *
		 * @param string email
		 * @return Promise<boolean>
		 */
		protected async validateEmailField(email: string): Promise<boolean> {
			if (!email) {
				this.emailError = 'Please enter your email address.';
				return false;
			}

			if (email.length < 8 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
				this.emailError = 'Please enter a valid email address.';
				return false;
			}

			if ((await this.isEmailAvailable()) === false) {
				this.emailError = 'This email address is already in use.';
				return false;
			}

			return true;
		}

		/**
		 * Navigates to the next step in the signup process
		 *
		 * @return void
		 */
		protected next(): void {
			this.$router.push({ params: { step: 'enter-password' } });
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		public async Handle_OnClickContinue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			// Validate the current form state
			const isFormValid = await this.validateFormInputs();

			// If validation passes, proceed to next step
			isFormValid && this.next();
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		public async Handle_OnClickSkip(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.$router.push({
				name: 'PageHome',
			});
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-signup-createaccount {
		.type-error {
			opacity: 1 !important;
		}
	}

</style>
